import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  endClientSubscriptionAPI,
  fetchPaymentHistory,
  pauseClientSubscriptionAPI,
  updatePaymentDetailsAPI,
  updatePaymentDetailsRetryAPI,
  extendClientSubscriptionAPI,
  updatePaymentDetailsRefundAPI
} from "../../api/clientPaymentTabApi";
import * as Endpoints from "../../entities/Endpoints";
import { fetchMembershipOverview } from "./membershipOverviewSlice";

// Async thunk to fetch payment history with pagination
export const getPaymentHistory = createAsyncThunk(
  `${Endpoints.GET_PAYMENT_HISTORY}`,
  async ({ clientId, pageNumber, pageSize }, { rejectWithValue }) => {
    try {
      const response = await fetchPaymentHistory(
        clientId,
        pageNumber,
        pageSize
      );

      return response.data;
    } catch (error) {
      return rejectWithValue(error || "Failed to fetch payment history.");
    }
  }
);

// Async thunk to update payment details
export const updatePaymentDetails = createAsyncThunk(
  `${Endpoints.UPDATE_PAYMENT_DETAILS}`,
  async (
    { clientId, paymentDetails, pageNumber, pageSize },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await updatePaymentDetailsAPI(clientId, paymentDetails);
      dispatch(getPaymentHistory({ clientId, pageNumber, pageSize }));

      // Call fetchMembershipOverview from another slice
      dispatch(fetchMembershipOverview(clientId));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "Failed to update payment details.");
    }
  }
);

// Async thunk to pause client subscription
export const pauseClientSubscription = createAsyncThunk(
  `${Endpoints.PAUSE_CLIENT_SUBSCRIPTION}`,
  async (
    { clientId, resumeDate, coachId, pageNumber, pageSize },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await pauseClientSubscriptionAPI({
        clientId,
        resumeDate,
        coachId,
      });
      dispatch(getPaymentHistory({ clientId, pageNumber, pageSize }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "Failed to pause subscription.");
    }
  }
);

// Async thunk to end client subscription
export const endClientSubscription = createAsyncThunk(
  `${Endpoints.END_CLIENT_SUBSCRIPTION}`,
  async (
    {
      clientId,
      endImmediately,
      endAfterBilling,
      coachId,
      pageNumber,
      pageSize,
    },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await endClientSubscriptionAPI({
        clientId,
        endImmediately,
        endAfterBilling,
        coachId,
      });
      dispatch(getPaymentHistory({ clientId, pageNumber, pageSize }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "Failed to end subscription.");
    }
  }
);


export const extendClientSubscription = createAsyncThunk(
  `${Endpoints.EXTEND_CLIENT_SUBSCRIPTION}`,
  async (
    { clientId, extendMonths,pageNumber,pageSize  },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await extendClientSubscriptionAPI({
        clientId,
        extendMonths
      });
       dispatch(getPaymentHistory({ clientId, pageNumber, pageSize }));
      return response.data;
    } catch (error) {
      return rejectWithValue(error || "Failed to extend subscription.");
    }
  }
);

export const retryPaymentDetails = createAsyncThunk(
  `${Endpoints.UPDATE_PAYMENT_DETAILS_RETRY}`,
  async (
    { clientId, paymentId },
    { rejectWithValue }
  ) => {
    try {
      const response = await updatePaymentDetailsRetryAPI({
        paymentId,clientId
      });

      if (response.data?.status === "FAILED") {
        return rejectWithValue(
          response.data?.Message || "Payment retry failed."
        );
      }

      return { ...response.data, paymentId };
    } catch (error) {
      return rejectWithValue(
        error || "Failed to retry payment details."
      );
    }
  }
);


export const refundPaymentDetails = createAsyncThunk(
  `${Endpoints.UPDATE_PAYMENT_DETAILS_REFUND}`,
  async (
    { clientId, paymentId },
    { rejectWithValue }
  ) => {
    try {
      const response = await updatePaymentDetailsRefundAPI({
        paymentId,clientId
      });

      if (response.data?.status === "FAILED") {
        return rejectWithValue(
          response.data?.Message || "Payment refund failed."
        );
      }

      return { ...response.data, paymentId };
    } catch (error) {
      return rejectWithValue(
        error || "Failed to refund payment details."
      );
    }
  }
);

const paymentSlice = createSlice({
  name: "payment",
  initialState: {
    paymentData: [],
    totalCount: 0,
    currentPage: 1,
    pageSize: 10,
    paymentDataLoading: false,
    updateLoading: false,
    error: null,
    updateSuccessMessage: "",
    updateFailedMessage: "",
    pauseSuccessMessage: "",
    pauseFailedMessage: "",
    endSuccessMessage: "",
    endFailedMessage: "",
    extendSuccessMessage: "",
    extendFailedMessage: "",

    retrySuccessMessage: "",
    retryFailedMessage: "",
    retryLoading: false,
    refundSuccessMessage: "",
    refundFailedMessage: "",
    refundLoading: false,
  },
  reducers: {
    // Reducer to handle pagination
    setPagination: (state, action) => {
      const { page, pageSize } = action.payload;
      if (pageSize !== state.pageSize) {
        state.paymentData = [];
        state.currentPage = 1;
      } else {
        state.currentPage = page;
      }
      state.pageSize = pageSize;
    },
    resetPagination: (state) => {
      state.currentPage = 1;
      state.pageSize = 10;
      state.paymentData = [];
      state.totalCount = 0;
    },
    clearUpdateMessage: (state) => {
      state.updateSuccessMessage = "";
      state.updateFailedMessage = "";
    },
    clearPauseMessage: (state) => {
      state.pauseSuccessMessage = "";
      state.pauseFailedMessage = "";
    },
    clearEndMessage: (state) => {
      state.endSuccessMessage = "";
      state.endFailedMessage = "";
    },
    clearRetryMessage: (state) => {
      state.retrySuccessMessage = "";
      state.retryFailedMessage = "";
    },
    clearExtendMessage: (state) => {
      state.extendSuccessMessage = "";
      state.extendFailedMessage = "";
    },
    clearRefundMessage: (state) => {
      state.refundSuccessMessage = "";
      state.refundFailedMessage = "";
    },
    
  },
  extraReducers: (builder) => {
    builder
      // Handling pending state for getPaymentHistory
      .addCase(getPaymentHistory.pending, (state) => {
        state.paymentDataLoading = true;
        state.error = null;
      })
      // Handling fulfilled state for getPaymentHistory
      .addCase(getPaymentHistory.fulfilled, (state, action) => {
        state.paymentData = action.payload?.payments || [];
        state.totalCount = action.payload?.totalCount || 0;
        state.paymentDataLoading = false;
        state.error = null;
      })
      // Handling rejected state for getPaymentHistory
      .addCase(getPaymentHistory.rejected, (state, action) => {
        state.paymentDataLoading = false;
        state.paymentData = [];
        state.error = action.payload || "Failed to fetch payment history.";
      })

      // Handling pending state for updatePaymentDetails
      .addCase(updatePaymentDetails.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.updateSuccessMessage = "";
        state.updateFailedMessage = "";
      })
      // Handling fulfilled state for updatePaymentDetails
      .addCase(updatePaymentDetails.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.updateSuccessMessage =
          action.payload?.message || "Payment details updated successfully.";
      })
      .addCase(updatePaymentDetails.rejected, (state, action) => {
        state.updateLoading = false;
        state.updateFailedMessage =
          action.payload || "Failed to update payment details.";
      })

      // Handling pending state for pauseClientSubscription
      .addCase(pauseClientSubscription.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.pauseSuccessMessage = "";
        state.pauseFailedMessage = "";
      })
      // Handling fulfilled state for pauseClientSubscription
      .addCase(pauseClientSubscription.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.pauseSuccessMessage =
          action.payload?.message ||
          "Paused Subscription details added successfully.";
      })
      .addCase(pauseClientSubscription.rejected, (state, action) => {
        state.updateLoading = false;
        state.pauseFailedMessage =
          action.payload || "Failed to pause subscription.";
      })

      // Handling pending state for endClientSubscription
      .addCase(endClientSubscription.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.endSuccessMessage = "";
        state.endFailedMessage = "";
      })
      // Handling fulfilled state for endClientSubscription
      .addCase(endClientSubscription.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.endSuccessMessage =
          action.payload?.message ||
          "End Subscription details added successfully.";
      })
      .addCase(endClientSubscription.rejected, (state, action) => {
        state.updateLoading = false;
        state.endFailedMessage =
          action.payload || "Failed to end subscription.";
      })

       // Handling pending state for extendClientSubscription
      .addCase(extendClientSubscription.pending, (state) => {
        state.updateLoading = true;
        state.error = null;
        state.extendSuccessMessage = "";
        state.extendFailedMessage = "";
      })
      // Handling fulfilled state for extendClientSubscription
      .addCase(extendClientSubscription.fulfilled, (state, action) => {
        state.updateLoading = false;
        state.extendSuccessMessage =
          action.payload?.message ||
          "Extend Subscription details added successfully.";
      })
      .addCase(extendClientSubscription.rejected, (state, action) => {
        state.updateLoading = false;
        state.extendFailedMessage =
          action.payload || "Failed to extend subscription.";
      })

      // Retry Payment
      .addCase(retryPaymentDetails.pending, (state) => {
        state.retryLoading = true;
        state.error = null;
        state.retrySuccessMessage = "";
        state.retryFailedMessage = "";
      })
      .addCase(retryPaymentDetails.fulfilled, (state, action) => {
        state.retryLoading = false;
        state.retrySuccessMessage =
          action.payload?.message || "Payment retried successfully.";
        const paymentId = action.payload?.paymentId ?? action.meta.arg.paymentId;
        const payment = state.paymentData.find(
          (p) => p.paymentId === paymentId
        );
        if (payment) {
          payment.payStatus = "Received";
        }
      })
      .addCase(retryPaymentDetails.rejected, (state, action) => {
        state.retryLoading = false;
        state.retryFailedMessage =
          action.payload || "Failed to retry payment.";
      })

      // Refund Payment
      .addCase(refundPaymentDetails.pending, (state) => {
        state.refundLoading = true;
        state.error = null;
        state.refundSuccessMessage = "";
        state.refundFailedMessage = "";
      })
      .addCase(refundPaymentDetails.fulfilled, (state, action) => {
        state.refundLoading = false;
        state.refundSuccessMessage =
          action.payload?.message || "Payment refund successfully.";
        const paymentId = action.payload?.paymentId ?? action.meta.arg.paymentId;
        const payment = state.paymentData.find(
          (p) => p.paymentId === paymentId
        );
        if (payment) {
          payment.payStatus = "Refunded";
          payment.isPaymentRefund = true;
        }
      })
      .addCase(refundPaymentDetails.rejected, (state, action) => {
        state.refundLoading = false;
        state.refundFailedMessage =
          action.payload || "Failed to refund payment.";
      });
  },
});

export const {
  setPagination,
  resetPagination,
  clearUpdateMessage,
  clearPauseMessage,
  clearEndMessage,
  clearRetryMessage,
  clearExtendMessage,
  clearRefundMessage,
} = paymentSlice.actions;
export default paymentSlice.reducer;
